import * as THREE from "three";
import { MATS } from "../materials.js";
import { makeRng } from "../builder.js";
import * as B from "./backspec.js";

/**
 * The flagstone patio at grade, and the family clutter that lives on it:
 * glass table and sling chairs by the house, the water table, a coiled hose,
 * a hand truck against the deck, and the odds and ends a lived-in garden
 * collects.
 *
 * The props double as cat furniture (jump 0.95, step 0.23).  Two chains:
 *
 *   stone 0.05 -> chair seat 0.45 (+0.40) -> chair back 0.90 (+0.45)
 *              -> glass table 0.72 (short hop down, or +0.27 from the seat)
 *
 *   stone 0.05 -> water table 0.51 (+0.46)
 *
 * That chain used to carry on to the kids' play kitchen, whose counter was
 * deliberately at 1.00 -- past the 0.95 jump from grade -- so that the water
 * table was the honest way up to it.  The kitchen has been taken out of the
 * garden; the water table stays, and is now the top of its own chain rather
 * than a step to something.
 *
 *   stone 0.05 -> boulder 0.51 (+0.46), a stepping stone toward the side bed.
 *
 * Everything else on the ground (hose, toys, watering can, hand truck) is
 * solid:false clutter the cat walks over, so nothing snags the player and
 * nothing can trap it -- the water table is a solid block to its rim, not an
 * open tray.
 */

// Prop heights the chains above rely on.
const SEAT = 0.45;      // sling chair seat top
const BACK = 0.90;      // sling chair back top
const TABLE = 0.72;     // glass table top
const TRAY = 0.51;      // water table rim

export function build(b) {
  const rng = makeRng(0xba7c0a7);

  flagstones(b, rng);

  // Cafe set at the near-left corner of the patio, beside the deck.
  glassTable(b, -2.60, -13.20);
  slingChair(b, -3.65, -13.20, Math.PI / 2 - 0.12);   // facing the table, +X
  slingChair(b, -1.60, -14.10, -0.84);                // pulled out, angled in

  // Play corner on the front strip, clear of the deck-step landing (x .55-2.45).
  waterTable(b, -1.85, -17.35);

  hoseCoil(b, -0.65, -16.15);
  handTruck(b, 2.85, -15.85);      // leaning on the deck face beside the steps
  wateringCan(b, 3.55, -17.45);
  sprayBottle(b, 3.28, -17.28);
  plantPot(b, 0.15, -11.95);       // tucked in the house/deck corner

  // Boulder where the patio edge gives way to the planting on the -X side.
  // Collider top ~0.51: one jump from the stone, then onward to the bed.
  b.rock(-4.55, 0, -15.55, 0.45, MATS.stone, { detail: 1 });
  b.mark("patio boulder", -4.55, 0.51, -15.55);

  // Stray toys on the stone -- walk-over clutter.
  b.sphere(-0.35, 0.05, -14.55, 0.065, MATS.toyRed, { segments: 8, rings: 6 });
  b.block(-1.15, 0.04, -17.72, 0.09, 0.09, 0.09, MATS.toyBlue, { solid: false });
  b.block(-1.04, 0.04, -17.64, 0.07, 0.07, 0.07, MATS.toyYellow, { solid: false });

  // Every solid prop must stay off the routes other modules rely on.
  for (const [nm, px, pz] of [
    ["table", -2.6, -13.2], ["kitchen", -3.3, -17.0], ["waterTable", -1.85, -17.35],
    ["boulder", -4.55, -15.55], ["pot", 0.15, -11.95],
  ]) {
    if (!B.clearOf(px, pz, 0.6)) console.warn(`patio: ${nm} blocks a route`);
  }
}

// ---------------------------------------------------------------------------
// Flagstone.  Two rectangles make the L around the deck: beside it against
// the house, and the strip across the front.  Each is a dark jointing layer
// with irregular buff slabs proud of it -- the gaps read as mortar joints.
// Stone tops land at 0.046..0.062, a couple of centimetres above the lawn and
// well under the 0.23 step, so the whole patio needs no colliders.
function flagstones(b, rng) {
  const P = B.PATIO;
  paveRect(b, rng, P.minX, B.DECK.minX, B.DECK.minZ, P.maxZ);   // beside deck
  paveRect(b, rng, P.minX, P.maxX, P.minZ, B.DECK.minZ);        // in front
}

function paveRect(b, rng, x0, x1, z0, z1) {
  b.slab((x0 + x1) / 2, 0.012, (z0 + z1) / 2, x1 - x0 - 0.08, z1 - z0 - 0.08,
         MATS.concreteDark, { thickness: 0.022, tile: 1.6 });
  const GAP = 0.04;
  let z = z0 + 0.02;
  while (z < z1 - 0.08) {
    let depth = 0.52 + rng() * 0.38;
    // A sliver of a final row would read as machine-cut; fold it into this one.
    if (z1 - z - GAP - depth < 0.34) depth = z1 - z - 0.03;
    let x = x0 - rng() * 0.5;          // negative start staggers the joints
    while (x < x1 - 0.12) {
      const w = 0.58 + rng() * 0.55;
      const sx0 = Math.max(x0 + 0.01, x) + rng() * 0.02;
      const sx1 = Math.min(x1 - 0.01, x + w) - rng() * 0.02;
      if (sx1 - sx0 > 0.20 && rng() > 0.03) {   // the odd missing stone
        const d = depth - rng() * 0.05;
        b.slab((sx0 + sx1) / 2, 0.02 + rng() * 0.006, z + depth / 2,
               sx1 - sx0, d, MATS.flagstone,
               { thickness: 0.026 + rng() * 0.008 });
      }
      x += w + GAP;
    }
    z += depth + GAP;
  }
}

// ---------------------------------------------------------------------------
// Glass-top table: dark glass on a pale metal frame.  The top carries the
// collider (0.69..0.72); legs and rim are too thin to be honest colliders and
// would only snag the cat walking underneath (0.69 of clearance).
function glassTable(b, x, z) {
  b.block(x, TABLE - 0.03, z, 0.92, 0.03, 0.92, MATS.carGlass);
  for (const sx of [-1, 1]) {
    b.bar(x + sx * 0.44, 0.66, z, 0.014, 0.86, "z", MATS.metalPale, { solid: false, segments: 6 });
    b.bar(x, 0.66, z + sx * 0.44, 0.014, 0.86, "x", MATS.metalPale, { solid: false, segments: 6 });
    for (const sz of [-1, 1]) {
      b.post(x + sx * 0.41, 0, z + sz * 0.41, 0.02, 0.68, MATS.metalPale, { solid: false, segments: 6 });
    }
  }
  b.mark("patio table", x, TABLE, z);
}

// Sling chair: grey fabric on a pale tube frame, slatted back.  Built in
// local space facing +Z and yawed, so the two chairs sit naturally around the
// table; the seat and back-top colliders are computed AABBs of the yawed
// rectangles, so what the cat stands on matches what is drawn.
function slingChair(b, x, z, yaw) {
  const cos = Math.cos(yaw), sin = Math.sin(yaw);
  const place = (g, mat) => {
    g.rotateY(yaw);
    g.translate(x, 0, z);
    b.add(g, mat, { tile: 0, collider: null });
  };
  const boxL = (w, h, d, lx, ly, lz, mat) =>
    place(new THREE.BoxGeometry(w, h, d).translate(lx, ly + h / 2, lz), mat);
  const tubeY = (r, h, lx, ly, lz) =>
    place(new THREE.CylinderGeometry(r, r, h, 6).translate(lx, ly + h / 2, lz), MATS.metalPale);
  const tubeZ = (r, len, lx, ly, lz) =>
    place(new THREE.CylinderGeometry(r, r, len, 6).rotateX(Math.PI / 2).translate(lx, ly, lz), MATS.metalPale);

  for (const sx of [-1, 1]) {
    tubeY(0.015, SEAT - 0.05, sx * 0.20, 0, 0.19);         // front legs
    tubeY(0.015, SEAT - 0.05, sx * 0.20, 0, -0.19);        // rear legs
    tubeY(0.015, BACK - 0.42, sx * 0.22, 0.42, -0.20);     // back posts
    tubeZ(0.014, 0.42, sx * 0.235, 0.60, -0.01);           // armrests
    tubeY(0.012, 0.15, sx * 0.235, 0.45, 0.17);            // arm fronts
  }
  boxL(0.46, 0.05, 0.44, 0, SEAT - 0.05, 0, MATS.plasticGrey);        // seat sling
  for (const ly of [0.50, 0.615, 0.73]) {
    boxL(0.42, 0.055, 0.022, 0, ly, -0.20, MATS.plasticGrey);         // back slats
  }
  boxL(0.44, 0.055, 0.028, 0, BACK - 0.055, -0.20, MATS.plasticGrey); // top slat

  b.collider(x, SEAT - 0.05, z, 0.50, 0.05, 0.50);   // seat, top 0.45
  // Back top at 0.90: AABB of the yawed 0.46 x 0.12 slat run.
  const bw = 0.46 * Math.abs(cos) + 0.12 * Math.abs(sin);
  const bd = 0.46 * Math.abs(sin) + 0.12 * Math.abs(cos);
  b.collider(x - 0.20 * sin, BACK - 0.06, z - 0.20 * cos, bw, 0.06, bd);
  b.mark("chair back " + (sin > 0 ? "L" : "R"), x - 0.20 * sin, BACK, z - 0.20 * cos);
}

// ---------------------------------------------------------------------------

// Water/sand table: blue tray on yellow legs.  The tray is one solid block to
// its rim -- a real open tray would be a pit a 0.24 m cat can fall into under
// the 0.23 step lip and never leave.  The 3 cm cosmetic lip above the collider
// is shallow enough to read right when the cat stands at the edge.
function waterTable(b, x, z) {
  for (const sx of [-1, 1]) for (const sz of [-1, 1]) {
    b.post(x + sx * 0.33, 0, z + sz * 0.20, 0.028, 0.42, MATS.toyYellow, { solid: false, segments: 6 });
  }
  b.block(x, 0.40, z, 0.82, 0.11, 0.58, MATS.toyBlue);              // top 0.51
  for (const s of [-1, 1]) {
    b.block(x + s * 0.395, TRAY, z, 0.03, 0.03, 0.58, MATS.toyBlue, { solid: false });
    b.block(x, TRAY, z + s * 0.275, 0.82, 0.03, 0.03, MATS.toyBlue, { solid: false });
  }
  b.slab(x, TRAY + 0.002, z, 0.74, 0.50, MATS.glass, { thickness: 0.012, tile: 0.8 });  // water

  // Bright toys in the tray, walk-over only.
  b.post(x + 0.28, TRAY + 0.01, z + 0.16, 0.055, 0.14, MATS.toyYellow, { solid: false, segments: 8 });
  b.post(x + 0.28, TRAY + 0.15, z + 0.16, 0.04, 0.11, MATS.toyYellow, { solid: false, segments: 8 });
  b.cone(x + 0.28, TRAY + 0.26, z + 0.16, 0.045, 0.06, MATS.toyRed, { segments: 8 });
  b.post(x - 0.22, TRAY + 0.01, z - 0.12, 0.032, 0.055, MATS.toyRed, { solid: false, segments: 6 });
  b.post(x - 0.10, TRAY + 0.01, z + 0.10, 0.03, 0.05, MATS.toyBlue, { solid: false, segments: 6 });
  b.mark("water table", x, TRAY, z);
}

// ---------------------------------------------------------------------------
// The orange hose, dropped in a loose coil with two trailing arcs toward the
// house.  Flat torus segments at slightly different radii and phases so it
// snakes rather than stacking into a perfect ring.  All of it is walk-over.
function hoseCoil(b, cx, cz) {
  const arcs = [
    [cx,        cz,        0.40, 0.4, 5.9, 0.063],   // [x, z, R, phase, arc, y]
    [cx + 0.03, cz - 0.02, 0.34, 1.3, 6.1, 0.069],
    [cx - 0.02, cz + 0.03, 0.28, 2.2, 5.8, 0.075],
    [cx + 0.37, cz + 0.43, 0.62, 4.9, 1.5, 0.063],   // tail toward the house
    [cx + 0.93, cz + 0.10, 0.50, 2.3, 1.2, 0.063],
  ];
  for (const [x, z, R, phase, arc, y] of arcs) {
    const g = new THREE.TorusGeometry(R, 0.013, 5, 16, arc);
    g.rotateX(Math.PI / 2);       // lay the ring flat on the stone
    g.rotateY(phase);
    g.translate(x, y, z);
    b.add(g, MATS.hoseOrange, { tile: 0, collider: null });
  }
  // Nozzle at the free end.
  const noz = new THREE.CylinderGeometry(0.014, 0.02, 0.10, 6);
  noz.rotateZ(Math.PI / 2);
  noz.rotateY(0.7);
  noz.translate(cx + 1.40, 0.055, cz - 0.22);
  b.add(noz, MATS.metalPale, { tile: 0, collider: null });
}

// Red hand truck leaning back against the deck face just past the steps
// (steps end at x 2.45; the wheels start at 2.64).  Tilted geometry with no
// colliders: nothing here is a standing surface, and an AABB around a leaning
// frame would be a wall of invisible air beside the step landing.
function handTruck(b, x, z) {
  const tilt = 0.33;               // top touches the deck face at z -15.45
  const lean = (g, mat) => {
    g.rotateX(tilt);
    g.translate(x, 0, z);
    b.add(g, mat, { tile: 0, collider: null });
  };
  for (const s of [-1, 1]) {
    lean(new THREE.BoxGeometry(0.035, 1.06, 0.035).translate(s * 0.175, 0.63, 0), MATS.toyRed);
  }
  for (const ly of [0.34, 0.64, 0.92]) {
    lean(new THREE.BoxGeometry(0.315, 0.03, 0.02).translate(0, ly, 0.005), MATS.toyRed);
  }
  lean(new THREE.CylinderGeometry(0.016, 0.016, 0.40, 6).rotateZ(Math.PI / 2).translate(0, 1.16, 0), MATS.metalDark);
  lean(new THREE.BoxGeometry(0.40, 0.015, 0.30).translate(0, 0.03, -0.19), MATS.toyRed);   // toe plate
  for (const s of [-1, 1]) {
    const w = new THREE.CylinderGeometry(0.10, 0.10, 0.045, 10);
    w.rotateZ(Math.PI / 2);
    w.translate(x + s * 0.21, 0.10, z + 0.02);
    b.add(w, MATS.metalDark, { tile: 0, collider: null });
  }
  b.bar(x, 0.088, z + 0.02, 0.012, 0.46, "x", MATS.metalDark, { solid: false, segments: 6 });
}

// ---------------------------------------------------------------------------
function wateringCan(b, x, z) {
  b.post(x, 0.04, z, 0.115, 0.26, MATS.plasticGreen, { solid: false, segments: 8, topRadius: 0.095 });
  const spout = new THREE.CylinderGeometry(0.012, 0.02, 0.30, 6);
  spout.rotateZ(0.9);
  spout.translate(x - 0.19, 0.20, z);
  b.add(spout, MATS.plasticGreen, { tile: 0, collider: null });
  const handle = new THREE.TorusGeometry(0.085, 0.011, 5, 10, Math.PI);
  handle.translate(x + 0.02, 0.30, z);
  b.add(handle, MATS.plasticGreen, { tile: 0, collider: null });
}

function sprayBottle(b, x, z) {
  b.post(x, 0.04, z, 0.032, 0.14, MATS.toyCream, { solid: false, segments: 6 });
  b.block(x, 0.18, z, 0.028, 0.05, 0.06, MATS.plasticGrey, { solid: false });
}

// Terracotta-ish pot with a boxwood ball, in the corner between the house
// wall and the deck.  The pot keeps its collider (0.28 tall -- an honest hop);
// the foliage stays soft.
function plantPot(b, x, z) {
  b.post(x, 0, z, 0.13, 0.28, MATS.edgePaver, { segments: 8, topRadius: 0.165 });
  b.post(x, 0.27, z, 0.14, 0.02, MATS.mulch, { solid: false, segments: 8 });
  b.sphere(x, 0.29, z, 0.17, MATS.boxwood, { segments: 8, rings: 6, squash: 0.88 });
}
